import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import GuessingLeaderboard from './GuessingLeaderboard';
import AnonymousPlayerCard from './AnonymousPlayerCard';
import { AuthContext } from '../../../contexts/AuthContext';
import { QuestionMark, EmojiEvents } from '@mui/icons-material';

const AnonymousPlayerGuessingGame = ({ tournamentId }) => {
  const [anonymousNames, setAnonymousNames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const { currentUser } = useContext(AuthContext);
  const [userGuesses, setUserGuesses] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);

        // Fetch tournament data
        const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
        if (!tournamentDoc.exists()) {
          throw new Error('Tournament not found');
        }

        const tournamentData = tournamentDoc.data();
        if (tournamentData.format !== 'guess') {
          throw new Error('This is not an anonymous tournament');
        }

        // Fetch anonymous assignments
        const assignmentsRef = collection(db, 'tournaments', tournamentId, 'anonymousAssignments');
        const assignmentsSnapshot = await getDocs(assignmentsRef);

        const namesData = [];
        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const assignmentData = assignmentDoc.data();
          if (assignmentData.nameRef) {
            const nameDoc = await getDoc(assignmentData.nameRef);
            if (nameDoc.exists()) {
              namesData.push({
                id: assignmentDoc.id,
                name: nameDoc.data().name,
                playerId: assignmentData.playerId,
                ...assignmentData,
              });
            }
          }
        }

        // Fetch players with user profiles
        const playersRef = collection(db, 'tournaments', tournamentId, 'players');
        const playersSnapshot = await getDocs(playersRef);
        const playersData = [];

        for (const playerDoc of playersSnapshot.docs) {
          const playerData = playerDoc.data();
          let userProfileData = null;

          // If player has a linked user profile, fetch it
          if (playerData.playerProfile) {
            try {
              const userProfileDoc = await getDoc(
                doc(db, 'userProfiles', playerData.playerProfile)
              );
              if (userProfileDoc.exists()) {
                userProfileData = userProfileDoc.data();
              }
            } catch (err) {
              console.error('Error fetching user profile:', err);
            }
          }

          playersData.push({
            id: playerDoc.id,
            ...playerData,
            userProfile: userProfileData,
          });
        }

        // Fetch clips
        const clipsQuery = query(
          collection(db, 'anonymousPlayerClips'),
          where('tournamentId', '==', tournamentId)
        );
        const clipsSnapshot = await getDocs(clipsQuery);
        const clipsMap = {};
        clipsSnapshot.docs.forEach((doc) => {
          const clipData = doc.data();
          clipsMap[clipData.anonymousNameId] = clipData.clipUrl;
        });

        // Add clip URLs to names
        const namesWithClips = namesData.map((name) => ({
          ...name,
          clipUrl: clipsMap[name.id] || null,
        }));

        // Fetch results
        const resultsQuery = query(
          collection(db, 'guessingResults'),
          where('tournamentId', '==', tournamentId)
        );
        const resultsSnapshot = await getDocs(resultsQuery);
        let resultsData = null;
        if (!resultsSnapshot.empty) {
          resultsData = resultsSnapshot.docs[0].data();
        }

        // Fetch user guesses
        if (currentUser) {
          const userGuessesQuery = query(
            collection(db, 'playerGuesses'),
            where('userId', '==', currentUser.uid),
            where('tournamentId', '==', tournamentId)
          );
          const userGuessesSnapshot = await getDocs(userGuessesQuery);

          let userGuessesData = {};
          if (!userGuessesSnapshot.empty) {
            const guessesDoc = userGuessesSnapshot.docs[0].data();
            guessesDoc.guesses?.forEach((guess) => {
              userGuessesData[guess.anonymousNameId] = guess.guessedPlayerId;
            });
          }
          setUserGuesses(userGuessesData);
        }

        setAnonymousNames(namesWithClips);
        setPlayers(playersData);
        setResults(resultsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching guessing game data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGameData();
  }, [tournamentId, currentUser]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        sx={{ bgcolor: '#123', color: 'white' }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: '#123', p: 2 }}>
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#123',
        color: 'white',
        borderRadius: { xs: 0, sm: 2 },
        overflow: 'hidden',
        width: '100%',
        mb: 4,
      }}
    >
      <Box
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h2"
          gutterBottom
          fontWeight="bold"
          sx={{ color: 'white' }}
        >
          Mystery Players - Who's Who?
        </Typography>
        <Typography
          variant={isMobile ? 'body1' : 'h6'}
          sx={{
            opacity: 0.8,
            color: 'white',
            mb: 2,
          }}
        >
          Test your knowledge! Can you identify the anonymous players?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Chip
            icon={<QuestionMark />}
            label={`${anonymousNames.length} Mystery Players`}
            color="secondary"
            sx={{ mx: { xs: 0, sm: 1 }, fontWeight: 'bold', mb: { xs: 1, sm: 0 } }}
          />
          <Chip
            icon={<EmojiEvents />}
            label={results?.isPublished ? 'Results Published' : 'Contest Active'}
            color={results?.isPublished ? 'success' : 'warning'}
            sx={{ mx: { xs: 0, sm: 1 }, fontWeight: 'bold' }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          py: 3,
          px: { xs: 2, sm: 3, md: 4 },
          bgcolor: '#123',
        }}
      >
        {results?.isPublished ? (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} variant="filled">
            <Typography variant="subtitle1" fontWeight="bold">
              Results have been published!
            </Typography>
            <Typography variant="body2">
              Check how many you got right and see who the mystery players really are.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} variant="filled">
            <Typography variant="subtitle1" fontWeight="bold">
              The guessing game is active!
            </Typography>
            <Typography variant="body2">
              Submit your guesses for who you think each anonymous player is.
            </Typography>
          </Alert>
        )}

        <GuessingLeaderboard tournamentId={tournamentId} darkMode={true} />

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            fontWeight="bold"
            sx={{ color: 'white' }}
          >
            {results?.isPublished ? 'Mystery Revealed!' : 'Make Your Guesses'}
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {results?.isPublished
              ? 'See how well you did at identifying the anonymous players.'
              : 'Can you guess the real identity of each anonymous player? Select from the dropdown and submit your guesses.'}
          </Typography>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={3}
          sx={{
            justifyContent: {
              xs: 'center',
              lg: 'flex-start',
            },
          }}
        >
          {anonymousNames.map((anonPlayer) => (
            <AnonymousPlayerCard
              key={anonPlayer.id}
              anonymousPlayer={anonPlayer}
              players={players}
              tournamentId={tournamentId}
              userGuess={userGuesses[anonPlayer.id]}
              results={results}
              darkMode={true}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AnonymousPlayerGuessingGame;
